import { useVbsCalculationCreateFromOldMutation } from "@/services/backend/vbs/calculations/api/calculation-api";
import { Button } from "@/shared/components/ui/button";
import { Copy } from "lucide-react";
import { v4 } from "uuid";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { useToast } from "@/shared/hooks/use-toast";
import { useEffect, useState } from "react";
import t from "@/lang/lang";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { BranchSelectFrom } from "@/services/backend/branch/components/branch-select-from";
import { ServiceObjectSelectFrom } from "@/services/backend/customers/components/service-object-select-from";

interface Props {
  calculationId: string;
  processId: string;
  onCopy: (calculationId: string) => void;
}

export function CopyCalculationButton({
  calculationId,
  processId,
  onCopy,
}: Props) {
  const [copy, { isLoading, error, reset }] =
    useVbsCalculationCreateFromOldMutation();
  const { data: process } = useProcessesProcessShowQuery({ id: processId });

  const branches = process?.branches ?? [];
  const serviceObjects = process?.serviceObjects ?? [];
  const [branchId, setBranchId] = useState(branches[0] ?? "");
  const [serviceObjectId, setServiceObjectId] = useState(
    serviceObjects[0] ?? "",
  );

  const disabled =
    !process ||
    !process.customerId ||
    isLoading ||
    branchId === "" ||
    serviceObjectId === "";

  const { toast } = useToast();

  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error, false),
        variant: "destructive",
      });
      reset();
    }
  }, [error, toast, reset]);

  const onClick = () => {
    if (disabled) {
      return;
    }

    const id = v4();
    copy({
      oldCalculationId: calculationId,
      id,
      processId: process.id,
      companyId: process.companyId,
      branchId,
      customerId: process.customerId!,
      serviceObjectId,
    })
      .unwrap()
      .then(() => onCopy(id));
  };

  return (
    <div className="flex flex-col items-end gap-2">
      {branches.length !== 1 && (
        <BranchSelectFrom
          branchIds={branches}
          value={branchId}
          onValueChange={setBranchId}
        />
      )}
      {serviceObjects.length !== 1 && (
        <ServiceObjectSelectFrom
          serviceObjectIds={serviceObjects}
          customerId={process?.customerId}
          value={serviceObjectId}
          onValueChange={setServiceObjectId}
        />
      )}
      <Button onClick={onClick} size="sm" disabled={disabled}>
        <Copy className="mr-2 h-4 w-4" />
        <span>{t("Daten übernehmen")}</span>
      </Button>
    </div>
  );
}
